import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  Button,
  Stack,
  CircularProgress,
} from "@mui/material";
import { parseISO, startOfDay, endOfDay } from "../../../utils/reports/pmData";

/**
 * Shared date-range input dialog for reports.
 *
 * Centralizes the start/end inputs, validation, and end-of-day normalization
 * that PMReportButton, PMValidationReport, and PMReportButton_FillTemplate each
 * used to reimplement.
 *
 * Props:
 *  - open        boolean
 *  - title       string
 *  - description string (optional helper text)
 *  - submitLabel string (default "Generate Report")
 *  - busy        boolean (disables inputs, shows spinner)
 *  - error       string (optional error text shown above actions)
 *  - onClose     () => void
 *  - onSubmit    ({ start: Date, end: Date }) => void
 *                start is start-of-day, end is end-of-day (inclusive)
 */
export default function ReportDateRangeDialog({
  open,
  title = "Generate Report",
  description,
  submitLabel = "Generate Report",
  busy = false,
  error = "",
  onClose,
  onSubmit,
}) {
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // Reset fields whenever the dialog is opened fresh.
  useEffect(() => {
    if (open) {
      setStartDate("");
      setEndDate("");
    }
  }, [open]);

  const canGenerate = useMemo(
    () => !!startDate && !!endDate && !busy,
    [startDate, endDate, busy]
  );

  const handleSubmit = () => {
    const s = parseISO(startDate);
    const e = parseISO(endDate);
    if (!s || !e) return;
    onSubmit({ start: startOfDay(s), end: endOfDay(e) });
  };

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        {description && (
          <DialogContentText sx={{ mb: 2 }}>{description}</DialogContentText>
        )}
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Start Date"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            disabled={busy}
            fullWidth
          />
          <TextField
            label="End Date"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            disabled={busy}
            fullWidth
          />
          {error && (
            <DialogContentText color="error" sx={{ fontSize: 13 }}>
              {error}
            </DialogContentText>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!canGenerate}
          startIcon={busy ? <CircularProgress size={16} /> : null}
        >
          {busy ? "Generating..." : submitLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
